import { cn } from '../cn.js';
import { ElapsedTimer } from './ElapsedTimer.js';

export interface RecordingIndicatorProps {
  /** When present, renders a live `ElapsedTimer` counting up from this instant beside the "REC"
   *  label — e.g. a training session's `startedAt`. Omit for a bare "recording" pill with no clock. */
  startedAt?: Date;
  className?: string;
}

/**
 * The "your camera is recording" pill for a training session player — a pulsing dot plus the
 * literal word "REC", so the state reads even without colour (see `Badge`'s `statusTone` comment
 * on why colour is never the only signal). Uses the `danger` token throughout, never a literal red
 * hex — this is the one place a "recording" indicator needs a colour that unambiguously reads as
 * "something is being captured," which is exactly what the danger token already means everywhere
 * else in this package.
 */
export function RecordingIndicator({ startedAt, className }: RecordingIndicatorProps) {
  return (
    <div
      role="status"
      aria-label="Recording"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-danger/30 bg-danger-subtle px-2 py-0.5 text-xs font-semibold text-danger',
        className,
      )}
    >
      <span className="relative flex size-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-danger" />
      </span>
      REC
      {startedAt && (
        <>
          <span aria-hidden="true">·</span>
          <ElapsedTimer startedAt={startedAt} format="HH:MM:SS" ariaLabel="Recording duration" />
        </>
      )}
    </div>
  );
}
